import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {AuthService} from "../_services/auth.service";
import {SnackbarService} from "../_services/snackbar.service";
import {Router} from "@angular/router";
import {User} from "../_types/user";
import {DynamicSettings} from "../_types/shared";
import {ImageCroppedEvent} from "ngx-image-cropper";

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrl: './settings.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class SettingsComponent implements OnInit, OnDestroy {
    imgURL: string | ArrayBuffer = 'assets/images/blankProfile.png';
    profileForm: FormGroup;
    passwordForm: FormGroup;
    accessibilityForm: FormGroup;
    dynamicSettings: DynamicSettings | null = null;
    photoFile: string | ArrayBuffer | null = null;
    user: User | undefined;
    showCropper: boolean = false;
    imageChangedEvent: Event | null = null;
    passwordLength: number = 8;

    constructor(private snackbarService: SnackbarService,
                private router: Router,
                private formBuilder: FormBuilder,
                private authService: AuthService,) {

        this.profileForm = this.formBuilder.group({
            first_name: [{value: '', disabled: true}, Validators.required],
            last_name: [{value: '', disabled: true}, [Validators.required, Validators.pattern('.*\\S.*')]],
            email: [{value: '', disabled: true}, Validators.required],
            username: [{value: '', disabled: true}, Validators.required],
            about_me: ['', Validators.required],
        });

        this.passwordForm = this.formBuilder.group({
            currentPassword: ['', Validators.required],
            password: ['', this.passwordValidators(8)],
            confirmPassword: ['', this.passwordValidators(8)],
        }, {validators: this.passwordMatchValidator})

        this.accessibilityForm = this.formBuilder.group({
            fontFamily: ['roboto'],
            fontSize: ['100'],
        })

        this.authService.getDynamicSettings().then((r): void => {
            this.dynamicSettings = r;
            if (r.minimum_password_length) {
                this.passwordLength = r.minimum_password_length;
                this.passwordForm.controls['password'].setValidators(this.passwordValidators(this.passwordLength));
                this.passwordForm.controls['confirmPassword'].setValidators(this.passwordValidators(this.passwordLength));
                this.passwordForm.updateValueAndValidity();
            }
        });
    }

    ngOnInit(): void {
        this.authService.getDynamicSettings().then((r): void => {
            if (r.is_raa) {
                this.profileForm.get('about_me')?.clearValidators();
            }
        });
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })
        this.authService.refreshUser().then((r): void => {
            this.user = r;
            if (this.user) {
                if (this.user.photo) {
                    this.imgURL = this.user.photo;
                }
                this.profileForm.patchValue({
                    first_name: this.user.first_name,
                    last_name: this.user.last_name,
                    about_me: this.user.about_me,
                    email: this.user.email,
                    username: this.user.username,
                })
                this.accessibilityForm.patchValue({
                    fontFamily: this.user.font_family,
                    fontSize: (this.user.font_size + 99),
                });
                if (this.user.role.role_type == 'admin') {
                    this.profileForm.controls['first_name'].enable();
                    this.profileForm.controls['last_name'].enable();
                    if (!this.user.is_sso_user) {
                        this.profileForm.controls['email'].enable();
                    }
                    this.profileForm.controls['username'].enable();
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.authService.refreshUser().then((r): void => {
            this.user = r;
            if (this.user) {
                // If they dont save the accessibility settings then go back to original on page leave
                if (this.user && document.documentElement.style.getPropertyValue('--font-size') != JSON.stringify(this.user.font_size) || document.documentElement.style.getPropertyValue('--font-family') != this.user.font_family) {
                    if (this.user?.font_size > 1) {
                        document.documentElement.style.setProperty('--font-size', ((this.user.font_size + 99) + '%'));
                    } else {
                        document.documentElement.style.setProperty('--font-size', '100%');
                    }
                    document.documentElement.style.setProperty('--font-family', this.user.font_family);
                }
            }
        });
    }

    imageCropped(event: ImageCroppedEvent): void {
        if (!event.blob) return;
        let file: File = new File([event.blob], "profile_picture.png");
        this.getBase64(file).then(data => {
            this.photoFile = data;
        });
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(event.blob);
        const that: this = this;
        reader.onloadend = function (): void {
            const base64data: string | ArrayBuffer | null = reader.result;
            if (base64data) {
                that.imgURL = base64data;
            }
        };
    }

    back(): void {
        if (history.length > 1) {
            history.back();
        } else {
            this.router.navigate(['/timeline']);
        }
    }

    formatLabel(value: number): string {
        // add %
        return `${value}%`;
    }

    removeImage(): void {
        this.imgURL = this.user?.photo || 'assets/images/blankProfile.png';
        this.showCropper = false;
    }

    onFileChanged(event: Event): void {
        const element = event.currentTarget as HTMLInputElement;
        const fileList: FileList | null = element.files;
        if (!fileList || fileList.length <= 0) return;

        const file: File = fileList[0];
        var mimeType: string = file.type;

        if (mimeType.match(/image\/*/) == null) {
            // check if image
            this.snackbarService.openSnackBar("Only images are supported.", 'error')
            return;
        }

        var reader: FileReader = new FileReader();
        reader.readAsDataURL(file);
        this.showCropper = true;
        this.imageChangedEvent = event;
        reader.onload = (_event): void => {
            if (reader.result) {
                this.imgURL = reader.result;
            } else {
                this.snackbarService.openSnackBar("Reader result returned null.", 'error')
            }
        }
        this.getBase64(file).then(data => {
            this.photoFile = data;
        });
    }

    setFont(event: string): void {
        document.documentElement.style.setProperty('--font-family', event);
    }

    setFontSize(event: number): void {
        document.documentElement.style.setProperty('--font-size', (event + '%'));
    }

    editPassword(): void {
        this.authService.updatePassword({
            current: this.passwordForm.value.currentPassword,
            new: this.passwordForm.value.password,
            new_repeat: this.passwordForm.value.confirmPassword,
        }).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('Password Updated', 'success')
                this.passwordForm.reset();
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    saveAccessibility(): void {
        // display slider as a percentage of scale rather than 0-30
        let font_size: number = (this.accessibilityForm.value.fontSize - 101)
        if (font_size < 1) {
            font_size = 1;
        }
        this.authService.patchUser({
            font_family: this.accessibilityForm.value.fontFamily,
            font_size: font_size
        }).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('Accessibility settings updated', 'success')
                this.authService.setUser(r)
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    saveProfile(): void {
        let user = {
            first_name: null,
            last_name: null,
            email: null,
            username: null,
            about_me: this.profileForm.value.about_me,
            photo: '',
        }
        if (this.photoFile) {
            user.photo = this.photoFile as string;
        }
        if (this.user?.role?.role_type == 'admin') {
            if (this.profileForm.value.first_name != this.user?.first_name) {
                user['first_name'] = this.profileForm.value.first_name
            }
            if (this.profileForm.value.last_name != this.user?.last_name) {
                user['last_name'] = this.profileForm.value.last_name?.trim()
            }
            if (this.profileForm.value.email != this.user?.email) {
                user['email'] = this.profileForm.value.email
            }
            if (this.profileForm.value.username != this.user?.username) {
                user['username'] = this.profileForm.value.username
            }
        }
        this.authService.patchUser(this.clean(user)).subscribe({
            next: r => {
                this.authService.setUser(r)
                this.user = r;
                this.removeImage();
                this.snackbarService.openSnackBar('Profile updated', 'success');
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    clean(obj: any) {
        // remove empty keys
        for (var propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
                delete obj[propName];
            }
        }
        return obj
    }

    getBase64(file: Blob): Promise<string | ArrayBuffer | null> {
        return new Promise(resolve => {
            const reader: FileReader = new FileReader();
            reader.onloadend = (): void => {
                resolve(reader.result);
            };
            return reader.readAsDataURL(file);
        });
    }

    private passwordValidators(length: number): ValidatorFn[] {
        const specialCharacters: string = `[!@#$%^&*()_+\\-=\\[\\]{};':"\\\\|,.<>\\/?]`;
        const pattern: string = `^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*${specialCharacters})(?!.*\\s).{${length},}$`;

        return [Validators.required, Validators.minLength(length), Validators.pattern(pattern)];
    }

    private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
        const password: string | null = group.get('password')?.value;
        const confirmPassword: string | null = group.get('confirmPassword')?.value;

        return password === confirmPassword ? null : {passwordMismatch: true};
    }
}
